// @flow
import type {Node} from 'react'
import React, {Fragment, useCallback, useState} from 'react'
import AddProposalMutation from 'mutations/AddProposalMutation'
import type {Response as AddProposalResponse} from 'mutations/AddProposalMutation'
import {graphql, useFragment} from 'react-relay'

import NewEditModal from 'components/ui/NewEditModal'
import Delete from 'components/proposals/controls/Delete'
import ProposeNowContainer from 'components/pages/Proposals/ProposeNowContainer'
import Body from './ProposalCreatorBody'
import type {
  ProposalCreatorModal_organization$key as OrganizationKey,
} from './__generated__/ProposalCreatorModal_organization.graphql'
import styles from './index.scss'

type Props = {|
  onCreated: () => Promise<void>,
  organization: OrganizationKey,
  toggle: () => Promise<void> | void,
  tensionDatabaseId: ?string,
  onProposalCreated?: (AddProposalResponse) => Promise<void>,
  circleId: string | null,
  providedTension?: ?string,
|}

async function createProposal({
  circleDatabaseId,
  setProposal,
  setProposalLoading,
  tensionDatabaseId,
  viewer,
  setWillAutoAccept,
}) {
  setProposalLoading(true)

  const response = await AddProposalMutation({
    circleDatabaseId,
    tensionDatabaseId,
    personDatabaseId: viewer.databaseId,
  })

  const newProposal = response.addProposal?.proposalEdge?.node
  if (newProposal) {
    setProposal(newProposal)
    setWillAutoAccept(newProposal.expectedResponseCount === 0)
  }

  setProposalLoading(false)

  return response
}

const organizationFragment = graphql`
  fragment 
  ProposalCreatorModal_organization on Organization {
    databaseId
    ...ProposalCreatorBody_organization
    
    viewer {
      databaseId
      ...ProposalCreatorBody_viewer
    }
  }
`

function ProposalCreatorModal({
  toggle,
  organization: organizationKey,
  onCreated,
  tensionDatabaseId,
  onProposalCreated,
  circleId,
  providedTension,
}: Props): null | Node {
  const [proposalLoading, setProposalLoading] = useState(false)
  const [deletionInFlight, setDeletionInFlight] = useState(false)
  const [proposal, setProposal] = useState(null)
  const [willAutoAccept, setWillAutoAccept] = useState(false)

  const saveAndClose = useCallback(() => (
    onCreated()
      .then(toggle)
  ), [toggle, onCreated])

  const organization = useFragment(organizationFragment, organizationKey)

  const viewer = organization.viewer

  const createProposalForCircleId = useCallback((circleDatabaseId: string) => {
    if (proposalLoading)
      return

    createProposal({
      setProposalLoading,
      viewer,
      setProposal,
      tensionDatabaseId,
      circleDatabaseId,
      setWillAutoAccept,
    }).then((response) => {
      if (onProposalCreated)
        return onProposalCreated(response)
      return null
    })
  }, [proposalLoading, viewer, tensionDatabaseId, onProposalCreated, setWillAutoAccept])

  const syncToggle = useCallback(() => {
    toggle()
  }, [toggle])

  const toggleDeleted = useCallback(() => {
    setDeletionInFlight(true)
    setProposal(null)
  }, [setDeletionInFlight, setProposal])

  const leftBlock = useCallback(() => {
    if (!proposal)
      return null

    return (
      <Delete
        buttonTag="button"
        className="btn btn-secondary btn-lg"
        label={I18n.t('react.components.edit_dialog.cancel')}
        onSuccess={toggle}
        proposal={proposal}
        toggleDeleted={toggleDeleted}
      />
    )
  }, [proposal, toggle, toggleDeleted])

  const extraButton = useCallback((saveButton) => {
    if (!proposal)
      return null

    return (
      <Fragment>
        <ProposeNowContainer proposal={proposal} toggle={toggle} />
        {saveButton}
      </Fragment>
    )
  }, [proposal, toggle])

  const explanation = (
    proposal && circleId && !willAutoAccept && (
      <div className={styles.explanation}>
        {I18n.t('tensions.react.proposal_button.explanation')}
      </div>
    )
  )

  if (!proposal && deletionInFlight)
    return null

  return (
    <NewEditModal
      isOpen
      keyboard={false}
      leftBlock={leftBlock}
      disableSave={!proposal}
      save={saveAndClose}
      saveTitle={I18n.t('proposals.rewrite.save')}
      size="wide"
      title={I18n.t('tensions.react.proposal_button.add')}
      toggle={syncToggle}
      extraButton={extraButton}
      explanation={explanation}
    >
      <div className="proposal-builder-modal-body test-proposal-builder-modal-body">
        <Body
          createProposalForCircleId={createProposalForCircleId}
          deletionInFlight={deletionInFlight}
          organization={organization}
          proposal={proposal}
          proposalLoading={proposalLoading}
          viewer={viewer}
          circleDatabaseId={circleId}
          providedTension={providedTension}
        />
      </div>
    </NewEditModal>
  )
}

ProposalCreatorModal.defaultProps = {
  onCreated: (): Promise<null> => Promise.resolve(null),
  tensionDatabaseId: null,
  circleId: null,
  onProposalCreated: (): Promise<void> => Promise.resolve(),
}

export default ProposalCreatorModal
